import { useState, useRef } from 'react'
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion'

const steps = [
  {
    letter: 'B',
    title: 'Brand Presence',
    subtitle: "Before generating leads, we strengthen the brand's digital credibility and visibility.",
    points: [
      'Content strategy, positioning, and communication that builds trust with the right audience.',
      'A strong brand presence ensures that when leads arrive, they already trust your expertise.',
    ],
  },
  {
    letter: 'L',
    title: 'Lead Generation',
    subtitle: 'Once the brand foundation is established, we activate high-performance Meta Ads campaigns focused on attracting the right prospects.',
    points: [
      'Our campaigns are built to generate qualified enquiries from people who are genuinely interested in your offering.',
      'The goal is quality leads, not just quantity.',
    ],
  },
  {
    letter: 'S',
    title: 'Sales Process',
    subtitle: 'The final step is turning leads into customers.',
    points: [
      'We help businesses create structured follow-up and conversion systems so every opportunity is handled professionally and efficiently.',
      'Because true growth happens when marketing leads and sales conversations work together.',
    ],
  },
]

const Visuals = [
  // B - Brand Presence
  () => (
    <div className="relative w-full h-full flex items-center justify-center min-h-[200px]">
      <motion.div className="absolute w-40 h-40 bg-primary/20 dark:bg-primary/30 rounded-full blur-2xl" animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 4, repeat: Infinity }} />
      <motion.div className="w-28 h-28 border-4 border-primary/30 dark:border-primary/50 rounded-full flex items-center justify-center relative z-10" animate={{ rotate: 360 }} transition={{ duration: 12, repeat: Infinity, ease: 'linear' }}>
        <motion.div className="w-16 h-16 bg-gradient-to-tr from-primary to-accent rounded-full shadow-[0_0_30px_rgba(26,69,176,0.5)]" animate={{ scale: [0.9, 1.1, 0.9] }} transition={{ duration: 2.5, repeat: Infinity }} />
        <div className="absolute -top-2 -right-2 w-6 h-6 bg-accent rounded-full blur-sm" />
        <div className="absolute -bottom-2 -left-2 w-4 h-4 bg-primary rounded-full blur-sm" />
      </motion.div>
    </div>
  ),
  // L - Lead Generation
  () => (
    <div className="relative w-full h-full flex items-center justify-center min-h-[200px]">
      <motion.div className="absolute w-40 h-40 bg-accent/20 dark:bg-accent/30 rounded-full blur-2xl" animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 4, repeat: Infinity }} />
      <div className="relative w-32 h-32 flex items-center justify-center z-10">
        {[...Array(3)].map((_, i) => (
          <motion.div key={i} className="absolute inset-0 border-2 border-accent/40 dark:border-accent/60 rounded-full" animate={{ scale: [0.5, 1.5], opacity: [1, 0] }} transition={{ duration: 2, repeat: Infinity, delay: i * 0.6 }} />
        ))}
        <div className="relative w-12 h-12 bg-gradient-to-br from-accent to-primary rounded-full shadow-[0_0_20px_rgba(255,215,0,0.6)] z-20 flex items-center justify-center">
          <div className="w-6 h-6 bg-white/30 rounded-full blur-[2px]" />
        </div>
      </div>
    </div>
  ),
  // S - Sales Process
  () => (
    <div className="relative w-full h-full flex flex-col items-center justify-center min-h-[200px]">
      <div className="absolute inset-0 bg-primary/10 dark:bg-accent/10 blur-3xl rounded-full"></div>
      <motion.div className="w-full h-14 bg-gradient-to-r from-primary/40 to-accent/40 rounded-xl backdrop-blur-md border border-white/30 dark:border-white/10 shadow-lg mb-4 relative z-10" animate={{ y: [0, -4, 0] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }} />
      <motion.div className="w-3/4 h-12 bg-gradient-to-r from-primary/50 to-accent/50 rounded-xl backdrop-blur-md border border-white/30 dark:border-white/10 shadow-lg mb-4 relative z-20" animate={{ y: [0, 4, 0] }} transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 0.5 }} />
      <motion.div className="w-1/2 h-10 bg-gradient-to-r from-primary/70 to-accent/70 rounded-xl backdrop-blur-md border border-white/30 dark:border-white/10 shadow-lg relative z-30" animate={{ y: [0, -4, 0] }} transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: 1 }} />
    </div>
  )
]

export function BLSMethod() {
  const [active, setActive] = useState(0)
  const step = steps[active]
  const containerRef = useRef<HTMLDivElement>(null)
  const ActiveVisual = Visuals[active]
  
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start end', 'end start']
  })
  
  const y = useTransform(scrollYProgress, [0, 1], [30, -30])

  return (
    <section ref={containerRef} className="section-pad px-4 sm:px-6 lg:px-8 bg-white dark:bg-navy-900 relative overflow-hidden">
      <div className="container-wide relative z-10">
        <motion.h2
          className="section-heading text-center mb-3"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          Our Process
        </motion.h2>
        <motion.p
          className="section-sub text-center max-w-2xl mx-auto mb-8 sm:mb-12"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.05 }}
        >
          The BLS Growth Method — At DigiSutra, we follow a structured approach designed to create predictable growth for businesses.
        </motion.p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          {steps.map((s, i) => (
            <motion.button
              key={s.letter}
              type="button"
              onClick={() => setActive(i)}
              className={`rounded-card p-6 sm:p-8 border-2 text-left transition-all min-h-[140px] sm:min-h-0 ${
                active === i
                  ? 'border-primary bg-primary/5 dark:bg-primary/20 shadow-card dark:shadow-card-dark ring-2 ring-primary/20 dark:ring-accent/20'
                  : 'border-slate-200 dark:border-slate-700 bg-slate-50/80 dark:bg-slate-800/50 hover:border-primary/40 dark:hover:border-accent/40'
              }`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.08 + i * 0.08, duration: 0.5 }}
              whileHover={{ scale: 1.03, y: -2 }}
              whileTap={{ scale: 0.98 }}
            >
              <span className="text-3xl sm:text-4xl font-bold text-accent">{s.letter}</span>
              <h3 className="mt-2 sm:mt-3 text-base sm:text-lg font-semibold text-slate-800 dark:text-slate-100">
                {s.title}
              </h3>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-400 line-clamp-2">{s.subtitle}</p>
            </motion.button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="mt-6 sm:mt-10 card-glass p-6 sm:p-8 md:p-10 flex flex-col md:flex-row items-center gap-8 md:gap-12"
          >
            <div className="flex-1">
              <h3 className="text-xl sm:text-2xl font-semibold text-primary dark:text-accent">{step.title}</h3>
              <p className="mt-3 sm:mt-4 text-slate-600 dark:text-slate-400 text-sm sm:text-base leading-relaxed">{step.subtitle}</p>
              <ul className="mt-6 space-y-4">
                {step.points.map((point, j) => (
                  <li key={j} className="flex items-start gap-3 text-slate-700 dark:text-slate-300 text-sm sm:text-base">
                    <span className="w-2 h-2 rounded-full bg-accent mt-2 shrink-0 shadow-[0_0_8px_rgba(255,215,0,0.8)]" />
                    {point}
                  </li>
                ))}
              </ul>
            </div>
            <motion.div 
              className="w-full md:w-1/3 max-w-[240px] flex-shrink-0 relative h-64 hidden md:flex flex-col items-center justify-center"
              style={{ y }}
            >
              <ActiveVisual />
            </motion.div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  )
}
